import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Package, Calendar, ShoppingBag, Menu, ArrowLeft, X } from 'lucide-react';

/**
 * LiteLayout - Mobile-optimized layout for Lite Mode pages
 * 
 * Provides:
 * - Minimal header with back button and title
 * - Bottom navigation consistent with LiteDashboard
 * - White background, proper padding
 */
const LiteLayout = ({ children, title = 'Aniyor', showBackButton = true, onBack }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-20">
      {/* Header */}
      <header className="bg-white px-4 py-3 shadow-sm flex items-center gap-3 sticky top-0 z-10 safe-area-pt">
        {showBackButton && (
          <button 
            onClick={handleBack}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 active:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={22} />
          </button>
        )}
        <div className="flex items-center gap-2 flex-1">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <h1 className="font-semibold text-gray-900 text-base truncate">{title}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 flex justify-around py-3 px-2 text-xs font-medium text-gray-400 z-20 safe-area-pb">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <Home size={20} />
          Home
        </button>
        <button 
          onClick={() => navigate('/orders')}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <Package size={20} />
          Orders
        </button>
        <button 
          onClick={() => navigate('/bookings')}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <Calendar size={20} />
          Bookings
        </button>
        <button 
          onClick={() => navigate('/listings')}
          className="flex flex-col items-center gap-1 text-indigo-600"
        >
          <ShoppingBag size={20} />
          Listings
        </button>
        <button 
          onClick={() => setShowMenu(true)}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <Menu size={20} />
          More
        </button>
      </nav>

      {/* Full Screen Menu Overlay */}
      {showMenu && (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-lg font-bold">Aniyor</span>
            </div>
            <button 
              onClick={() => setShowMenu(false)}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-4 space-y-2">
            <button 
              onClick={() => { navigate('/dashboard'); setShowMenu(false); }}
              className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-gray-700"
            >
              Dashboard
            </button>
            <button 
              onClick={() => { navigate('/listings'); setShowMenu(false); }}
              className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-gray-700"
            >
              My Listings
            </button>
            <button 
              onClick={() => { navigate('/orders'); setShowMenu(false); }}
              className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-gray-700"
            >
              Orders
            </button>
            <button 
              onClick={() => { navigate('/vendor/settings'); setShowMenu(false); }}
              className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-gray-700"
            >
              Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiteLayout;
